import React, { useState } from 'react';
import { createActivityLogEntry, updateProspectingRecord, getLinkedContactId, getBrokerFromProspect } from '../api/monday';

// ── Constants ──────────────────────────────────────────────────────────────

const ACTIVITY_TYPES = [
  'Prospect call',
  'Owner call',
  'Text',
  'In Person',
  'Email',
  'Other',
];

// Context-sensitive outcomes per activity type
const OUTCOMES_BY_TYPE = {
  'Prospect call': ['Reached', 'No answer / left message'],
  'Owner call':    ['Reached', 'No answer / left message'],
  'Text':          ['Reached', 'No response'],
  'In Person':     ['Met', 'No show'],
  'Email':         ['Email sent'],
  'Other':         ['Reached', 'No answer / left message'],
};

const SNOOZE_OPTIONS = [
  { label: '3 days',   days: 3 },
  { label: '1 week',   days: 7 },
  { label: '2 weeks',  days: 14 },
  { label: '1 month',  days: 30 },
  { label: '3 months', days: 90 },
  { label: 'Custom',   days: null },
];

const OUTCOME_COLORS = {
  'Reached':                  { bg: '#E1F5EE', color: '#085041', border: '#6ECFAA' },
  'No answer / left message': { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
  'No response':              { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
  'Email sent':               { bg: '#EEEDFE', color: '#3C3489', border: '#A09CD6' },
  'Met':                      { bg: '#E1F5EE', color: '#085041', border: '#6ECFAA' },
  'No show':                  { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
};

// ── Helpers ────────────────────────────────────────────────────────────────

function addDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Styles ─────────────────────────────────────────────────────────────────

const s = {
  page: {
    fontFamily: "'Inter', 'Segoe UI', system-ui, Arial, sans-serif",
    maxWidth: '720px',
    margin: '0 auto',
    minHeight: '100vh',
    backgroundColor: '#f5f4f1',
    display: 'flex',
    flexDirection: 'column',
  },
  nav: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e8e6e0',
    position: 'sticky', top: 0, zIndex: 10,
  },
  backBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '22px', color: '#374151', padding: '0 4px', lineHeight: 1,
  },
  navTitle: { fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 },
  navSub: { fontSize: '13px', color: '#9ca3af', marginLeft: 'auto' },

  body: { flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' },

  section: {
    backgroundColor: '#fff', borderRadius: '12px',
    padding: '16px 18px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  sectionTitle: {
    fontSize: '11px', fontWeight: '700', color: '#9ca3af',
    textTransform: 'uppercase', letterSpacing: '0.07em',
    marginBottom: '12px',
  },
  required: { color: '#dc2626', marginLeft: '3px' },

  // Pill grids
  pillGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  pill: {
    padding: '8px 14px', borderRadius: '99px',
    fontSize: '14px', fontWeight: '500', cursor: 'pointer',
    border: '1.5px solid', transition: 'all 0.12s',
    background: 'none',
  },

  // Notes
  textarea: {
    width: '100%', minHeight: '90px',
    border: '1.5px solid #e5e7eb', borderRadius: '8px',
    padding: '10px 12px', fontSize: '14px', color: '#111827',
    fontFamily: 'inherit', resize: 'vertical',
    boxSizing: 'border-box', outline: 'none',
  },

  // Snooze preview
  snoozePreview: {
    marginTop: '10px', fontSize: '13px', color: '#6b7280',
    fontStyle: 'italic',
  },

  // Custom date input
  dateInput: {
    marginTop: '10px', padding: '8px 12px',
    border: '1.5px solid #e5e7eb', borderRadius: '8px',
    fontSize: '14px', color: '#111827', width: '100%',
    boxSizing: 'border-box',
  },

  // Validation hint
  hint: { fontSize: '12px', color: '#dc2626', marginTop: '6px' },

  // Save button area
  footer: { padding: '0 0 24px' },
  saveBtn: {
    width: '100%', padding: '15px',
    borderRadius: '10px', border: 'none',
    fontSize: '16px', fontWeight: '700', cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  cancelBtn: {
    width: '100%', padding: '12px', marginTop: '8px',
    borderRadius: '10px', border: '1px solid #e5e7eb',
    fontSize: '14px', fontWeight: '500', cursor: 'pointer',
    background: '#fff', color: '#374151',
  },

  saving: { textAlign: 'center', padding: '40px 20px', color: '#6b7280' },
  errorBox: { background: '#fef2f2', borderRadius: '8px', padding: '14px', color: '#b91c1c', fontSize: '13px' },
  successBox: {
    textAlign: 'center', padding: '60px 20px',
    background: '#fff', borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
};

// ── Main component ─────────────────────────────────────────────────────────

export default function LogActivity({ prospectItem, brokerName, onSaved, onCancel }) {
  const [activityType, setActivityType] = useState('Prospect call');
  const [outcome, setOutcome] = useState('');
  const [snoozeOption, setSnoozeOption] = useState(null); // label string
  const [customDate, setCustomDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  const contactId = getLinkedContactId(prospectItem);
  console.log('[LogActivity] prospectItem.id:', prospectItem?.id, '| contactId:', contactId);

  // Derive broker name from the item's Main P.O.C. column (more reliable than session,
  // and correctly attributes the activity even when an admin is viewing as someone else).
  const itemBrokerName = getBrokerFromProspect(prospectItem, prospectItem._mainPocColId) || brokerName;

  // Compute the actual snooze date string
  const snoozeDate = (() => {
    if (!snoozeOption) return null;
    if (snoozeOption === 'Custom') return customDate || null;
    const opt = SNOOZE_OPTIONS.find((o) => o.label === snoozeOption);
    return opt ? addDays(opt.days) : null;
  })();

  const canSave = outcome && snoozeDate;

  async function handleSave() {
    if (!canSave) { setShowValidation(true); return; }
    setSaving(true);
    setError(null);
    try {
      await Promise.all([
        createActivityLogEntry({
          contactItemId: contactId,
          prospectingItemName: prospectItem.name,
          activityType,
          outcome,
          notes,
          nextFollowUpDate: snoozeDate,
          stageAtLog: prospectItem.column_values?.find(c => c.id === 'status1')?.text || '',
          brokerName: itemBrokerName,
        }),
        updateProspectingRecord(prospectItem.id, snoozeDate),
      ]);
      setSaved(true);
      setTimeout(() => onSaved(), 1200);
    } catch (err) {
      setError(err.message || 'Save failed. Please try again.');
      setSaving(false);
    }
  }

  if (saving && !error) {
    return (
      <div style={s.page}>
        <div style={s.saving}>
          {saved ? '✅ Saved! Returning to queue…' : 'Saving activity…'}
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      {/* Nav */}
      <div style={s.nav}>
        <button style={s.backBtn} onClick={onCancel}>←</button>
        <div>
          <p style={{ ...s.navTitle, margin: 0 }}>Log Activity</p>
        </div>
        <span style={s.navSub}>{prospectItem.name.split('|')[0].trim()}</span>
      </div>

      <div style={s.body}>
        {error && <div style={s.errorBox}><strong>Error:</strong> {error}</div>}

        {/* Activity type */}
        <div style={s.section}>
          <p style={s.sectionTitle}>Activity Type</p>
          <div style={s.pillGrid}>
            {ACTIVITY_TYPES.map((type) => {
              const selected = activityType === type;
              return (
                <button
                  key={type}
                  style={{
                    ...s.pill,
                    backgroundColor: selected ? '#534AB7' : '#fff',
                    color: selected ? '#fff' : '#374151',
                    borderColor: selected ? '#534AB7' : '#e8e6e0',
                  }}
                  onClick={() => { setActivityType(type); setOutcome(''); }}
                >
                  {type}
                </button>
              );
            })}
          </div>
        </div>

        {/* Outcome */}
        <div style={s.section}>
          <p style={s.sectionTitle}>
            Outcome <span style={s.required}>*</span>
          </p>
          <div style={s.pillGrid}>
            {(OUTCOMES_BY_TYPE[activityType] || []).map((o) => {
              const selected = outcome === o;
              const colors = OUTCOME_COLORS[o];
              return (
                <button
                  key={o}
                  style={{
                    ...s.pill,
                    backgroundColor: selected ? colors.bg : '#fff',
                    color: selected ? colors.color : '#6b7280',
                    borderColor: selected ? colors.border : '#e5e7eb',
                    fontWeight: selected ? '700' : '500',
                  }}
                  onClick={() => setOutcome(o)}
                >
                  {o}
                </button>
              );
            })}
          </div>
          {showValidation && !outcome && (
            <p style={s.hint}>Select an outcome before saving.</p>
          )}
        </div>

        {/* Snooze */}
        <div style={s.section}>
          <p style={s.sectionTitle}>
            Next Follow Up <span style={s.required}>*</span>
          </p>
          <div style={s.pillGrid}>
            {SNOOZE_OPTIONS.map((opt) => {
              const selected = snoozeOption === opt.label;
              return (
                <button
                  key={opt.label}
                  style={{
                    ...s.pill,
                    backgroundColor: selected ? '#534AB7' : '#fff',
                    color: selected ? '#fff' : '#374151',
                    borderColor: selected ? '#534AB7' : '#e8e6e0',
                  }}
                  onClick={() => { setSnoozeOption(opt.label); if (opt.label !== 'Custom') setCustomDate(''); }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {snoozeOption === 'Custom' && (
            <input
              type="date"
              style={s.dateInput}
              value={customDate}
              min={addDays(1)}
              onChange={(e) => setCustomDate(e.target.value)}
            />
          )}

          {snoozeDate && snoozeOption !== 'Custom' && (
            <p style={s.snoozePreview}>Follow up on {formatDate(snoozeDate)}</p>
          )}

          {showValidation && !snoozeDate && (
            <p style={s.hint}>Set a follow-up date before saving.</p>
          )}
        </div>

        {/* Notes */}
        <div style={s.section}>
          <p style={s.sectionTitle}>Notes <span style={{ color: '#9ca3af', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></p>
          <textarea
            style={s.textarea}
            placeholder="What happened? What did they say?"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {/* Save */}
        <div style={s.footer}>
          <button
            style={{
              ...s.saveBtn,
              backgroundColor: canSave ? '#1D9E75' : '#e5e7eb',
              color: canSave ? '#fff' : '#9ca3af',
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
            onClick={handleSave}
          >
            Save Activity
          </button>
          <button style={s.cancelBtn} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
